import { initTsrReactQuery } from '@ts-rest/react-query/v5';
import { initClient } from '@ts-rest/core';
import type { ClientArgs } from '@ts-rest/core';
import type { PropsWithChildren, ReactElement } from 'react';
import type {
  TsRestReactQueryClient,
  TsRestReactQueryHooksContainer,
} from '@ts-rest/react-query/v5';
import { deliveryGatewayContract } from '@flote/contracts/http';
import { QueryClient } from '@tanstack/react-query';
import { apiKeyManager } from '../auth';
import { getDeliveryAdminApiBaseUrl } from '../lib/api-base-url';

export interface DeliveryApiClientConfig {
  baseUrl: string;
}

let currentConfig: DeliveryApiClientConfig = {
  baseUrl: getDeliveryAdminApiBaseUrl(),
};

type DeliveryContract = typeof deliveryGatewayContract;
type DeliveryTsrClient = {
  ReactQueryProvider: ({ children }: PropsWithChildren) => ReactElement;
} & TsRestReactQueryHooksContainer<DeliveryContract, ClientArgs> & {
  useQueryClient: () => TsRestReactQueryClient<DeliveryContract, ClientArgs>;
  initQueryClient: (queryClient: QueryClient) => TsRestReactQueryClient<DeliveryContract, ClientArgs>;
};

function createDeliveryClient(config: DeliveryApiClientConfig): DeliveryTsrClient {
  return initTsrReactQuery(deliveryGatewayContract, {
    baseUrl: config.baseUrl,
    baseHeaders: {
      'Content-Type': 'application/json',
      'x-app-source': 'flote-delivery-admin',
      Authorization: () => {
        const apiKey = apiKeyManager.getApiKey();
        return apiKey ? `Bearer ${apiKey}` : '';
      },
    },
  });
}

// Singleton React Query client
export let deliveryTsr: DeliveryTsrClient = createDeliveryClient(currentConfig);

// Plain ts-rest client for imperative calls
function createPlainClient(config: DeliveryApiClientConfig) {
  return initClient(deliveryGatewayContract, {
    baseUrl: config.baseUrl,
    baseHeaders: {
      'Content-Type': 'application/json',
      'x-app-source': 'flote-delivery-admin',
      Authorization: () => {
        const apiKey = apiKeyManager.getApiKey();
        return apiKey ? `Bearer ${apiKey}` : '';
      },
    },
  });
}

export let deliveryClient = createPlainClient(currentConfig);

// Singleton query client
export const deliveryQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 1000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error && typeof error === 'object' && 'response' in error) {
          const errorWithResponse = error as { response?: { status?: number } };
          if (errorWithResponse.response?.status === 401) {
            return false;
          }
        }
        return failureCount < 3;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/** Re-initialize the delivery API client with new config */
export function initializeDeliveryApiClient(config: DeliveryApiClientConfig): void {
  currentConfig = config;
  deliveryTsr = createDeliveryClient(config);
  deliveryClient = createPlainClient(config);
}

export { deliveryGatewayContract };
